/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { LogService } from '../services/log.service.js';
import { isString } from './identity.js';
export function hasClassnames(el, ...classNames) {
    return classNames.filter((cn) => el.classList.contains(cn)).length === classNames.length;
}
export function addClassnames(el, ...classNames) {
    classNames.forEach((cn) => {
        el.classList.add(cn);
    });
    return el;
}
export function removeClassnames(el, ...classNames) {
    classNames.forEach((cn) => {
        el.classList.remove(cn);
    });
    return el;
}
export function removeClassnamesUnless(el, classnamesToRemove, classnamesToKeep) {
    const filteredClassnamesToRemove = classnamesToRemove.filter(cn => classnamesToKeep.indexOf(cn) < 0);
    return removeClassnames(el, ...filteredClassnamesToRemove);
}
export function updateElementStyles(el, ...styleTuples) {
    styleTuples.forEach(([styleKey, value]) => {
        el.style[styleKey] = value;
    });
    return el;
}
export function unsetElementStyles(el, ...styleProperties) {
    styleProperties.forEach(prop => {
        el.style[prop] = '';
    });
    return el;
}
export function pxToRem(pxValue) {
    return `calc((${pxValue} / var(--cds-global-base)) * 1rem)`;
}
export function getCssPropertyValue(propertyName, el = document.body, pseudoSelectorModifier = null) {
    try {
        return getComputedStyle(el, pseudoSelectorModifier).getPropertyValue(propertyName).trim();
    }
    catch (e) {
        LogService.warn('Container element passed to getCustomPropertyValue must be an element.');
        return '';
    }
}
export function setCssPropertyValue(propertyName, value, el = document.body) {
    try {
        if (value === '' || value === null || value === false) {
            el.style.removeProperty(propertyName);
        }
        else {
            el.style.setProperty(propertyName, value);
        }
    }
    catch (e) {
        LogService.warn('Container element passed to getCustomPropertyValue must be an element.');
    }
}
export function isCssPropertyName(str) {
    return !!str && isString(str) && str.slice(0, 2) === '--';
}
export function supportsAdoptingStyleSheets() {
    return (window.ShadowRoot &&
        (window.ShadyCSS === undefined || window.ShadyCSS.nativeShadow) &&
        'adoptedStyleSheets' in Document.prototype &&
        'replace' in CSSStyleSheet.prototype);
}
